import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal, MaskLines } from "@/components/Reveal";
import { CONTACT, SERVICES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full bg-transparent border-0 border-b border-blue/25 py-3 font-body text-blue placeholder:text-slate/60 focus:outline-none focus:border-amber-deep transition-colors";

export default function Contact() {
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", years_in_business: "", website: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill) {
      setForm((f) => ({ ...f, message: prefill, interest: location.state?.interest || f.interest }));
      const el = document.querySelector('[data-testid="contact-form"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [location.state]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email, and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you, your request is on its way to Scott.");
      setForm({ name: "", email: "", phone: "", interest: "", years_in_business: "", website: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call 575-420-3926.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="bg-cream grain pt-36 pb-16 md:pt-48 md:pb-20 relative">
        <div className="container-editorial relative z-10 max-w-4xl">
          <Reveal>
            <p className="eyebrow text-amber-deep mb-8 flex items-center gap-3">
              <span className="w-10 h-px bg-amber-deep" /> Begin the Conversation
            </p>
          </Reveal>
          <h1 className="font-serif font-bold text-5xl md:text-7xl lg:text-8xl text-blue leading-[0.98] tracking-tight">
            <MaskLines lines={["Book a"]} delay={0.1} />
            <MaskLines lines={["consultation."]} className="italic text-amber-deep" delay={0.26} />
          </h1>
          <Reveal delay={0.5}>
            <p className="font-body text-lg text-charcoal/75 mt-8 max-w-2xl leading-relaxed">
              Whether you're launching, growing, funding, or planning your exit, start with a
              conversation. Tell Scott where you are, and he'll map the path from here.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream pb-24 md:pb-32">
        <div className="container-editorial grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-20">
          <Reveal>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <label className="eyebrow text-[0.58rem] text-slate block mb-2">Name *</label>
                  <input data-testid="contact-name" className={FIELD} value={form.name} onChange={update("name")} placeholder="Your full name" />
                </div>
                <div>
                  <label className="eyebrow text-[0.58rem] text-slate block mb-2">Email *</label>
                  <input data-testid="contact-email" type="email" className={FIELD} value={form.email} onChange={update("email")} placeholder="you@company.com" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <label className="eyebrow text-[0.58rem] text-slate block mb-2">Phone</label>
                  <input data-testid="contact-phone" className={FIELD} value={form.phone} onChange={update("phone")} placeholder="(000) 000-0000" />
                </div>
                <div>
                  <label className="eyebrow text-[0.58rem] text-slate block mb-2">I'm interested in</label>
                  <select data-testid="contact-interest" className={`${FIELD} cursor-pointer`} value={form.interest} onChange={update("interest")}>
                    <option value="">Select a focus…</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.code}>{s.code}</option>
                    ))}
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <label className="eyebrow text-[0.58rem] text-slate block mb-2">Years in business</label>
                  <input data-testid="contact-years" className={FIELD} value={form.years_in_business} onChange={update("years_in_business")} placeholder="e.g. 8" />
                </div>
                <div>
                  <label className="eyebrow text-[0.58rem] text-slate block mb-2">Website</label>
                  <input data-testid="contact-website" className={FIELD} value={form.website} onChange={update("website")} placeholder="yourcompany.com" />
                </div>
              </div>
              <div>
                <label className="eyebrow text-[0.58rem] text-slate block mb-2">Message *</label>
                <textarea
                  data-testid="contact-message"
                  rows={4}
                  className={`${FIELD} resize-none`}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell Scott where your business is today and where you'd like it to go."
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="group relative overflow-hidden bg-blue text-cream px-9 py-4 eyebrow text-xs flex items-center gap-2 disabled:opacity-60"
              >
                <span className="relative z-10 flex items-center gap-2">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />}
                  {loading ? "Sending…" : "Send to Scott"}
                </span>
                <span className="absolute inset-0 bg-amber-deep translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="bg-blue text-cream p-10 md:p-12 grain relative h-full">
              <div className="relative z-10">
                <p className="eyebrow text-amber-bright mb-8">Direct Line</p>
                <div className="space-y-8">
                  <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="group flex items-center gap-4">
                    <span className="w-11 h-11 border border-cream/20 flex items-center justify-center group-hover:border-amber-bright transition-colors">
                      <Phone size={17} className="text-amber-bright" />
                    </span>
                    <div>
                      <p className="eyebrow text-[0.55rem] text-cream/40">Cell</p>
                      <p className="font-serif font-bold text-2xl text-cream group-hover:text-amber-bright transition-colors">{CONTACT.phone}</p>
                    </div>
                  </a>
                  <a href={CONTACT.emailHref} data-testid="contact-email-link" className="group flex items-center gap-4">
                    <span className="w-11 h-11 border border-cream/20 flex items-center justify-center group-hover:border-amber-bright transition-colors">
                      <Mail size={17} className="text-amber-bright" />
                    </span>
                    <div>
                      <p className="eyebrow text-[0.55rem] text-cream/40">Email</p>
                      <p className="font-serif font-bold text-xl text-cream group-hover:text-amber-bright transition-colors break-all">{CONTACT.email}</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-4">
                    <span className="w-11 h-11 border border-cream/20 flex items-center justify-center">
                      <MapPin size={17} className="text-amber-bright" />
                    </span>
                    <div>
                      <p className="eyebrow text-[0.55rem] text-cream/40">Based In</p>
                      <p className="font-serif font-bold text-xl text-cream">{CONTACT.location}</p>
                      <p className="font-body text-sm text-cream/50">{CONTACT.serviceArea}</p>
                    </div>
                  </div>
                </div>

                <div className="h-px bg-amber/30 my-10" />
                <p className="font-serif italic text-xl text-cream/80 leading-snug">
                  &ldquo;The exit is the summit every other decision climbs toward.&rdquo;
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
